import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Draggable } from 'react-beautiful-dnd'
import { Dropdown } from 'antd'
import { Icon } from '@iconify/react'

import { getCardColorStyle, getStoryStatusLabel, getStoryMapFilterClass, isUntitledName, normalizeStoryStatus } from '../utils'
import { CARD_HEIGHT, STEP_WIDTH } from '../constants'
import useCloseDropdownOnScroll from '../hooks/useCloseDropdownOnScroll'
import CardMetaIcons from './CardMetaIcons'
import styles from '../../ProjectStoryMapBoard.module.css'

const DRAG_CLICK_THRESHOLD = 5

const StoryCardContent = ({
    story,
    dragHandleProps,
    isDragging,
    isEditing,
    editDraft,
    titleInputRef,
    onDraftChange,
    onCommitTitle,
    onOpen,
    storyMenu,
    isHighlighted = false,
    isFilterActive = false,
    isFilterMatchCurrent = false,
}) => {
    const pointerStartRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const closeMenu = useCallback(() => setMenuOpen(false), [])
    useCloseDropdownOnScroll(menuOpen, closeMenu)

    const handlePointerDown = (event) => {
        pointerStartRef.current = { x: event.clientX, y: event.clientY }
    }

    const handleOpen = (event) => {
        // Draft cards exist only client-side — the detail drawer would 404.
        if (isDragging || isEditing || story.isDraft) return

        const start = pointerStartRef.current
        if (start) {
            const dx = Math.abs(event.clientX - start.x)
            const dy = Math.abs(event.clientY - start.y)
            if (dx > DRAG_CLICK_THRESHOLD || dy > DRAG_CLICK_THRESHOLD) {
                return
            }
        }

        onOpen?.(story.id)
    }

    const cardColorStyle = getCardColorStyle(story.color || story.accentColor)
    const storyStatus = normalizeStoryStatus(story.status)
    const storyStatusLabel = getStoryStatusLabel(storyStatus)
    const filterClass = getStoryMapFilterClass(
        isFilterActive,
        story.matchesFilter,
        styles
    )

    const isFilterMatch =
        isFilterActive && story.matchesFilter === true

    return (
        <div
            className={styles.cardActionShell}
            {...(isFilterMatch ? { 'data-story-map-filter-match': 'true' } : {})}
        >
            <div
                className={`${styles.storyCard} ${
                    isDragging ? styles.storyCardDragging : ''
                } ${isEditing ? styles.storyCardEditing : ''} ${
                    isHighlighted ? styles.storyCardHighlighted : ''
                } ${story.isDraft ? styles.storyCardAgentDraft : ''} ${filterClass} ${
                    isFilterMatchCurrent ? styles.filterMatchFocused : ''
                }`}
                style={cardColorStyle}
                data-story-map-story-id={story.id}
            >
            <div
                {...(!isEditing ? dragHandleProps : {})}
                className={styles.storyCardDragSurface}
            >
                {!story.isDraft && (storyMenu?.(story.id) ?? []).length > 0 ? (
                    <Dropdown
                        menu={{ items: storyMenu(story.id) }}
                        trigger={['click']}
                        placement="bottomRight"
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        getPopupContainer={() => document.body}
                        destroyPopupOnHide
                    >
                        <button
                            type="button"
                            className={styles.storyMenuBtn}
                            aria-label="Story options"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Icon icon="mdi:dots-vertical" width={16} />
                        </button>
                    </Dropdown>
                ) : null}
                {isEditing ? (
                    <div className={styles.storyCardBody}>
                        <div className={styles.storyCardTop}>
                            <span
                                className={`${styles.storyStatus} ${
                                    styles[`storyStatus_${storyStatus}`] || ''
                                }`}
                            >
                                {storyStatusLabel}
                            </span>
                        </div>
                        <input
                            ref={titleInputRef}
                            type="text"
                            className={styles.storyTitleInput}
                            value={editDraft}
                            placeholder="Untitled story"
                            aria-label="Story name"
                            onChange={(event) => onDraftChange(event.target.value)}
                            onBlur={(event) =>
                                onCommitTitle(story.id, event.target.value, {
                                    fromBlur: true,
                                })
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onCommitTitle(story.id, event.currentTarget.value)
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div
                        className={styles.storyCardBody}
                        role="button"
                        tabIndex={0}
                        onPointerDown={handlePointerDown}
                        onClick={handleOpen}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                if (!isDragging) onOpen?.(story.id)
                            }
                        }}
                    >
                        <div className={styles.storyCardTop}>
                            <span
                                className={`${styles.storyStatus} ${
                                    styles[`storyStatus_${storyStatus}`] || ''
                                }`}
                            >
                                {storyStatusLabel}
                            </span>
                            {story.isDraft ? (
                                <span className={styles.agentDraftBadge}>AI</span>
                            ) : null}
                            <CardMetaIcons item={story} />
                        </div>
                        <span
                            className={`${styles.storyCardTitle} ${
                                isUntitledName(story.title) ? styles.untitledLabel : ''
                            }`}
                        >
                            {story.title || 'Untitled story'}
                        </span>
                    </div>
                )}
            </div>
            </div>
        </div>
    )
}

const StoryCard = ({
    story,
    storyIndex,
    isEditing,
    editDraft,
    titleInputRef,
    onDraftChange,
    onCommitTitle,
    onOpen,
    storyMenu,
    isHighlighted = false,
    isFilterActive = false,
    isFilterMatchCurrent = false,
    readOnly = false,
}) => (
    <Draggable
        key={story.id}
        draggableId={`story-${story.id}`}
        index={storyIndex}
        isDragDisabled={
            readOnly ||
            isEditing ||
            Boolean(story.isDraft) ||
            (isFilterActive && story.matchesFilter === false)
        }
    >
        {(provided, snapshot) => {
            const isDragging = snapshot.isDragging
            const isDropAnimating = snapshot.isDropAnimating
            const { style: dragStyle, ...dragProps } = provided.draggableProps

            // Portaled to document.body — board CSS vars are unavailable, use px.
            const dragChrome =
                isDragging || isDropAnimating
                    ? {
                          zIndex: 10050,
                          width: STEP_WIDTH,
                          minWidth: STEP_WIDTH,
                          maxWidth: STEP_WIDTH,
                          height: CARD_HEIGHT,
                          minHeight: CARD_HEIGHT,
                          maxHeight: CARD_HEIGHT,
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                          ...(isDropAnimating
                              ? {
                                    opacity: 0,
                                    transitionDuration: '0.001s',
                                }
                              : null),
                      }
                    : undefined

            const node = (
                <div
                    ref={provided.innerRef}
                    {...dragProps}
                    style={{
                        ...dragStyle,
                        ...dragChrome,
                    }}
                    className={`${styles.storyDragItem} ${
                        isDragging || isDropAnimating
                            ? styles.storyDragItemDragging
                            : ''
                    }`}
                >
                    <StoryCardContent
                        story={story}
                        dragHandleProps={provided.dragHandleProps}
                        isDragging={isDragging || isDropAnimating}
                        isEditing={isEditing}
                        editDraft={editDraft}
                        titleInputRef={titleInputRef}
                        onDraftChange={onDraftChange}
                        onCommitTitle={onCommitTitle}
                        onOpen={onOpen}
                        storyMenu={
                            isDragging || isDropAnimating ? undefined : storyMenu
                        }
                        isHighlighted={isHighlighted}
                        isFilterActive={isFilterActive}
                        isFilterMatchCurrent={isFilterMatchCurrent}
                    />
                </div>
            )

            if ((isDragging || isDropAnimating) && typeof document !== 'undefined') {
                return createPortal(node, document.body)
            }

            return node
        }}
    </Draggable>
)

export default StoryCard
